import React, { useEffect, useState } from "react";
import { Button } from "../../../components/CustomControl/Button";
import {
  apiCall,
  apiOption,
  LoginUserInfo,
  language,
} from "../../../actions/api";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Typography, TextField } from "@material-ui/core";
import CustomTable from "components/CustomTable/CustomTable";

const InvoiceAddModal = (props) => {
  const serverpage = "billgenerate"; // this is .php server page

  const [currentRow, setCurrentRow] = useState({});
  const [invoiceList, setInvoiceList] = useState([]);
  const [errorObject, setErrorObject] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const UserInfo = LoginUserInfo();

  // Filter states
  const [invoiceStartDate, setInvoiceStartDate] = useState("");
  const [invoiceEndDate, setInvoiceEndDate] = useState("");

  const [currBuyerId, setCurrBuyerId] = useState("");
  const [currMerchantId, setCurrMerchantId] = useState("");
  const [currBusinessLineId, setCurrBusinessLineId] = useState("");

  useEffect(() => {
    setCurrentRow(props.currentRow);
  }, []);

  /** Unique option list built from a column of the searched invoices */
  function uniqueOptionList(field, blankLabel) {
    const names = invoiceList
      .map((item) => (item[field] === null || item[field] === undefined ? "" : String(item[field]).trim()))
      .filter((name) => name !== "");

    const uniqueNames = Array.from(new Set(names)).sort((a, b) =>
      a.localeCompare(b)
    );

    return [{ id: "", name: blankLabel }].concat(
      uniqueNames.map((name) => ({ id: name, name: name }))
    );
  }

  const BuyerList = React.useMemo(
    () => uniqueOptionList("GeneralDescription11", "Select Buyer"),
    [invoiceList]
  );

  const MerchantList = React.useMemo(
    () => uniqueOptionList("GeneralDescription14", "Select Merchant"),
    [invoiceList]
  );

  const BusinessLineList = React.useMemo(
    () => uniqueOptionList("AnalysisCode3", "Select Business Line"),
    [invoiceList]
  );

  /** Rows shown in the table after applying the Buyer/Merchant/Business Line filter */
  const filteredInvoiceList = React.useMemo(() => {
    return invoiceList.filter((item) => {
      const buyer = item.GeneralDescription11
        ? String(item.GeneralDescription11).trim()
        : "";
      const merchant = item.GeneralDescription14
        ? String(item.GeneralDescription14).trim()
        : "";
      const businessLine = item.AnalysisCode3
        ? String(item.AnalysisCode3).trim()
        : "";

      return (
        (currBuyerId === "" || buyer === currBuyerId) &&
        (currMerchantId === "" || merchant === currMerchantId) &&
        (currBusinessLineId === "" || businessLine === currBusinessLineId)
      );
    });
  }, [invoiceList, currBuyerId, currMerchantId, currBusinessLineId]);

  /** Changing a dropdown clears selection so hidden rows can not stay selected */
  function clearSelection() {
    setInvoiceList((prev) =>
      prev.map((item) => ({ ...item, IsSelected: false }))
    );
  }

  function searchInvoices() {
    if (!props.currentRow.CustomerId) {
      props.masterProps.openNoticeModal({
        isOpen: true,
        msg: "Customer is required to search invoices",
        msgtype: 0,
      });
      return;
    }

    setIsLoading(true);
    let params = {
      action: "getUnbilledInvoices",
      lan: language(),
      UserId: UserInfo.UserId,
      CustomerId: props.currentRow.CustomerId,
      InvoiceStartDate: invoiceStartDate,
      InvoiceEndDate: invoiceEndDate,
      PaymentId: props.currentRow.id,
    };

    apiCall
      .post(serverpage, { params }, apiOption())
      .then((res) => {
        setIsLoading(false);
        const list = res.data.datalist
          ? res.data.datalist.map((item) => ({
              ...item,
              IsSelected: false,
            }))
          : [];
        setInvoiceList(list);
        setCurrBuyerId("");
        setCurrMerchantId("");
        setCurrBusinessLineId("");
      })
      .catch((err) => {
        setIsLoading(false);
        console.error("Error fetching invoices:", err);
      });
  }

  const getRowKey = (item) =>
    item.InvoiceItemId ?? item.TransactionId ?? item.id;

  const handleChangeCheck = (e, row) => {
    const checked = e.target.checked;
    const rowKey = getRowKey(row);

    const updatedItems = invoiceList.map((item) => {
      const itemKey = getRowKey(item);
      if (itemKey === rowKey) {
        return { ...item, IsSelected: checked };
      }
      return item;
    });

    setInvoiceList(updatedItems);
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    const visibleKeys = new Set(filteredInvoiceList.map(getRowKey));

    setInvoiceList((prev) =>
      prev.map((item) =>
        visibleKeys.has(getRowKey(item))
          ? { ...item, IsSelected: checked }
          : item
      )
    );
  };

  function addInvoicesToBill() {
    const selected = filteredInvoiceList.filter((item) => item.IsSelected);

    if (selected.length === 0) {
      props.masterProps.openNoticeModal({
        isOpen: true,
        msg: "Please select at least one invoice",
        msgtype: 0,
      });
      return;
    }

    let params = {
      action: "addInvoicesToBill",
      lan: language(),
      UserId: UserInfo.UserId,
      PaymentId: props.currentRow.id,
      invoices: selected,
    };

    apiCall.post(serverpage, { params }, apiOption()).then((res) => {
      props.masterProps.openNoticeModal({
        isOpen: true,
        msg: res.data.message,
        msgtype: res.data.success,
      });

      if (res.data.success === 1) {
        props.modalCallback("addedit");
      }
    });
  }

  function modalClose() {
    props.modalCallback("close");
  }

  const columnList = [
    {
      field: "custom",
      label: (
        <input
          type="checkbox"
          checked={
            filteredInvoiceList.length > 0 &&
            filteredInvoiceList.every((item) => item.IsSelected)
          }
          onChange={handleSelectAll}
          style={{ width: "18px", height: "18px" }}
        />
      ),
      width: "3%",
      align: "center",
      visible: true,
      sort: false,
      filter: false,
    },
    { field: "rownumber", label: "SL", align: "center", width: "3%" },
    {
      field: "TransactionDate",
      label: "Invoice Date",
      width: "5%",
      align: "left",
      visible: true,
      sort: true,
      filter: true,
    },

    {
      field: "TransactionReference",
      label: "Invoice Number",
      width: "13%",
      align: "left",
      visible: true,
      sort: true,
      filter: true,
    },
    //     {
    //   field: "GeneralDescription9",
    //   label: "Report Number",
    //   width: "8%",
    //   align: "left",
    //   visible: true,
    //   sort: true,
    //   filter: true,
    // },
    {
      field: "AnalysisCode3",
      label: "Business Line",
      width: "8%",
      align: "left",
      visible: true,
      sort: true,
      filter: true,
    },
    {
      field: "GeneralDescription11",
      label: "Buyer Name",
      // width: "10%",
      align: "left",
      visible: true,
      sort: true,
      filter: true,
    },

    
    {
      field: "TransactionAmount",
      label: "Amount in FC",
      width: "5%",
      align: "right",
      visible: true,
      sort: true,
      filter: true,
      bottomcalc: "sum",
      type: "number",
    },
    {
      field: "ExchangeRate",
      label: "Ex. Rate",
      align: "right",
      width: "4%",
      visible: true,
      sort: false,
      filter: true,
      type: "number",
    },
    {
      field: "BaseAmount",
      label: "Amount in BDT",
      align: "right",
      width: "6%",
      visible: true,
      sort: false,
      filter: true,
      type: "number",
      bottomcalc: "sum",
    },



    {
      field: "GeneralDescription17",
      label: "Style No.",
      // width: "10%",
      align: "left",
      visible: true,
      sort: true,
      filter: true,
    },
    // {
    //   field: "OrderNumber",
    //   label: "Order No.",
    //   width: "10%",
    //   align: "left",
    //   visible: true,
    //   sort: true,
    //   filter: true,
    // },

    {
      field: "GeneralDescription14",
      label: "Merchandiser Name",
      width: "13%",
      align: "left",
      visible: true,
      sort: true,
      filter: true,
    },
    {
      field: "GeneralDescription20",
      label: "Service Type",
      width: "10%",
      align: "left",
      visible: true,
      sort: true,
      filter: true,
      // type: "number",
    },
  ];

  /** Action from table row buttons*/
  function actioncontrol(rowData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <input
          id="IsSelected"
          name="IsSelected"
          type="checkbox"
          style={{ width: "18px", height: "18px" }}
          checked={
            rowData.IsSelected === true ||
            rowData.IsSelected === 1 ||
            rowData.IsSelected === "1"
          }
          onChange={(e) => handleChangeCheck(e, rowData)}
        />
      </div>
    );
  }

  // Memoize selected buyer
  const selectedBuyer = React.useMemo(() => {
    return BuyerList.find((list) => list.id === currBuyerId) || BuyerList[0];
  }, [BuyerList, currBuyerId]);

  // Memoize selected merchant
  const selectedMerchant = React.useMemo(() => {
    return (
      MerchantList.find((list) => list.id === currMerchantId) || MerchantList[0]
    );
  }, [MerchantList, currMerchantId]);

  // Memoize selected business line
  const selectedBusinessLine = React.useMemo(() => {
    return (
      BusinessLineList.find((list) => list.id === currBusinessLineId) ||
      BusinessLineList[0]
    );
  }, [BusinessLineList, currBusinessLineId]);

  return (
    <>
      {/* <!-- INVOICE MODAL START --> */}
      <div id="invoiceModal" className="modal">
        {/* <!-- Modal content --> */}
        <div className="modal-content" style={{ width: "90%", maxWidth: "1200px" }}>
          <div className="modalHeader">
            <h4>Add Invoice to Bill</h4>
          </div>

          <div className="contactmodalBody pt-10">
            <label>Invoice Start Date</label>
            <input
              type="date"
              id="InvoiceStartDate"
              name="InvoiceStartDate"
              placeholder="Start Date"
              value={invoiceStartDate}
              onChange={(e) => setInvoiceStartDate(e.target.value)}
            />

            <label>Invoice End Date</label>
            <input
              type="date"
              id="InvoiceEndDate"
              name="InvoiceEndDate"
              placeholder="End Date"
              value={invoiceEndDate}
              onChange={(e) => setInvoiceEndDate(e.target.value)}
            />
          </div>

          <div
            className="contactmodalBody pt-10"
            style={{
              display: "flex",
              justifyContent: "end",
              paddingRight: "16px",
            }}
          >
            <Button
              label={isLoading ? "Searching..." : "Search"}
              class={"btnSave"}
              onClick={searchInvoices}
              disabled={isLoading}
            />
          </div>

          <div className="contactmodalBody pt-10">
            <label>Buyer</label>
            <Autocomplete
              autoHighlight
              disableClearable
              className="chosen_dropdown"
              id="BuyerId"
              name="BuyerId"
              autoComplete
              disabled={invoiceList.length === 0}
              options={BuyerList}
              getOptionLabel={(option) => option.name}
              value={selectedBuyer}
              onChange={(event, valueobj) => {
                setCurrBuyerId(valueobj ? valueobj.id : "");
                clearSelection();
              }}
              renderOption={(option) => (
                <Typography className="chosen_dropdown_font">
                  {option.name}
                </Typography>
              )}
              renderInput={(params) => (
                <TextField {...params} variant="standard" fullWidth />
              )}
            />

            <label>Merchant</label>
            <Autocomplete
              autoHighlight
              disableClearable
              className="chosen_dropdown"
              id="MerchantId"
              name="MerchantId"
              autoComplete
              disabled={invoiceList.length === 0}
              options={MerchantList}
              getOptionLabel={(option) => option.name}
              value={selectedMerchant}
              onChange={(event, valueobj) => {
                setCurrMerchantId(valueobj ? valueobj.id : "");
                clearSelection();
              }}
              renderOption={(option) => (
                <Typography className="chosen_dropdown_font">
                  {option.name}
                </Typography>
              )}
              renderInput={(params) => (
                <TextField {...params} variant="standard" fullWidth />
              )}
            />
          </div>

          <div className="contactmodalBody pt-10">
            <label>Business Line</label>
            <Autocomplete
              autoHighlight
              disableClearable
              className="chosen_dropdown"
              id="BusinessLineId"
              name="BusinessLineId"
              autoComplete
              disabled={invoiceList.length === 0}
              options={BusinessLineList}
              getOptionLabel={(option) => option.name}
              value={selectedBusinessLine}
              onChange={(event, valueobj) => {
                setCurrBusinessLineId(valueobj ? valueobj.id : "");
                clearSelection();
              }}
              renderOption={(option) => (
                <Typography className="chosen_dropdown_font">
                  {option.name}
                </Typography>
              )}
              renderInput={(params) => (
                <TextField {...params} variant="standard" fullWidth />
              )}
            />
          </div>

          <div style={{ padding: "16px" }}>
            <div style={{ display: "flex", gap: "20px", marginBottom: "16px", flexWrap: "wrap" }}>
              {/* Total Section */}
              <div style={{ display: "flex", gap: "15px", padding: "10px", backgroundColor: "#fafafa", borderRadius: "10px", border: "2px solid #e0e0e0" }}>
                <div style={{ padding: "12px 20px", backgroundColor: "#f3e5f5", borderRadius: "8px", minWidth: "120px" }}>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Total Invoice</div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "#7b1fa2" }}>
                    {filteredInvoiceList.length}
                  </div>
                </div>
                <div style={{ padding: "12px 20px", backgroundColor: "#e3f2fd", borderRadius: "8px", minWidth: "180px" }}>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Total Amount (FC)</div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "#1565c0" }}>
                    {filteredInvoiceList.reduce((sum, item) => sum + (parseFloat(item.TransactionAmount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={{ padding: "12px 20px", backgroundColor: "#e8f5e9", borderRadius: "8px", minWidth: "180px" }}>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Total Amount (BDT)</div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "#2e7d32" }}>
                    {filteredInvoiceList.reduce((sum, item) => sum + (parseFloat(item.BaseAmount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* Selected Section */}
              <div style={{ display: "flex", gap: "15px", padding: "10px", backgroundColor: "#fff8e1", borderRadius: "10px", border: "2px solid #ffcc80" }}>
                <div style={{ padding: "12px 20px", backgroundColor: "#ede7f6", borderRadius: "8px", minWidth: "120px" }}>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Selected Invoice</div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "#512da8" }}>
                    {filteredInvoiceList.filter(item => item.IsSelected).length}
                  </div>
                </div>
                <div style={{ padding: "12px 20px", backgroundColor: "#fff3e0", borderRadius: "8px", minWidth: "180px" }}>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Selected Amount (FC)</div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "#e65100" }}>
                    {filteredInvoiceList.filter(item => item.IsSelected).reduce((sum, item) => sum + (parseFloat(item.TransactionAmount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={{ padding: "12px 20px", backgroundColor: "#fce4ec", borderRadius: "8px", minWidth: "180px" }}>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Selected Amount (BDT)</div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "#c2185b" }}>
                    {filteredInvoiceList.filter(item => item.IsSelected).reduce((sum, item) => sum + (parseFloat(item.BaseAmount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
            <div className="" style={{ maxHeight: "350px", overflow: "auto" }}>
              <CustomTable
                columns={columnList}
                rows={filteredInvoiceList.length > 0 ? filteredInvoiceList : {}}
                actioncontrol={actioncontrol}
                ispagination={false}
              />
            </div>
          </div>

          <div className="modalItem">
            <Button label={"Close"} class={"btnClose"} onClick={modalClose} />
            <Button
              label={"Add Selected Invoices"}
              class={"btnSave"}
              onClick={addInvoicesToBill}
            />
          </div>
        </div>
      </div>
      {/* <!-- INVOICE MODAL END --> */}
    </>
  );
};

export default InvoiceAddModal;
